import { useMemo, useCallback } from 'react';
import { useAppSelector } from 'app/store';
import { selectUserCompanies, selectColumnPreferences } from 'app/store/userCompaniesSlice';
import { useColumnVisibility } from 'app/hooks/transactions/useColumnVisibility';
import { useIsEnabled } from 'app/shared-components/feature-flags/FeatureFlagProvider';
import { agaveDropdownModule } from 'app/pages/overview/tabs/transactions/AgaveTransactions/AgaveDropdownColumnModule';
import { flexDropdownModule } from 'app/pages/overview/tabs/transactions/FlexTransactions/FlexDropdownColumnModule';
import { jobCodeDropdownModule } from 'app/pages/overview/tabs/transactions/JobCodesTransactions/JobCodeDropdownColumnModule';
import {
  getMissingRequiredFields,
  reconcilePreferences,
  EQUIPMENT_CODING_COLUMN_IDS,
} from 'app/util/columnVisibility';
import type {
  CodingColumnId,
  CodingSystemType,
  TransactionColumnId,
  ColumnPreferenceEntry,
} from 'app/util/columnVisibility';
import type {
  DropdownColumnModule,
  ModuleColumnProps,
} from 'app/pages/overview/tabs/transactions/UniversalTransactionsTable';

export interface UseCodingSystemConfigResult {
  /** Helper to check if a specific column is visible (single source of truth) */
  isVisible: (columnId: CodingColumnId) => boolean;
  /** Get the display label for a column (custom label or default) */
  getLabel: (columnId: CodingColumnId) => string;
  /** Visible column IDs in admin-configured order (without user preferences) */
  visibleColumnIds: CodingColumnId[];
  /** Whether any coding columns are available */
  hasCodingColumns: boolean;
  /** Coding system type identifier (null if no coding system) */
  codingSystemType: CodingSystemType | null;
  /** Whether this is an Agave client */
  isAgaveClient: boolean;
  /** Whether this is a Flex account */
  isFlexAccount: boolean;
  /** Whether this is a Job Code account */
  isJobCodeAccount: boolean;
  /** Coding system dropdown column module */
  dropdownModule: DropdownColumnModule | undefined;
  /** Props to pass to the dropdown module (uses user-preferred column order) */
  moduleColumnProps: ModuleColumnProps;
  /**
   * All user-visible configurable columns in user-preferred order (unified flat list).
   * Excludes locked columns (select, card, merchantName, amount, date).
   * Used to drive the full table column assembly.
   */
  orderedVisibleIds: TransactionColumnId[];
  /** Get missing required field labels for a transaction. Returns empty array if all satisfied. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getMissingRequired: (transaction: Record<string, any> | null | undefined) => string[];
}

export interface UseCodingSystemConfigOptions {
  /** When true, modules return disabled/non-interactive cell renderers */
  readOnly?: boolean;
}

/**
 * Hook for coding system configuration and column visibility.
 *
 * Determines which coding system is active (Agave, Flex, JobCode) and returns
 * the appropriate dropdown module and visibility configuration.
 *
 * @param options - Optional config. Pass `{ readOnly: true }` for non-interactive tables (e.g. reimbursements).
 * @returns Coding system configuration and visibility
 */
export function useCodingSystemConfig(options?: UseCodingSystemConfigOptions): UseCodingSystemConfigResult {
  const { readOnly } = options ?? {};
  const {
    isVisible: rawIsVisible,
    getLabel,
    visibleColumnIds: rawVisibleColumnIds,
    codingSystemType,
    hasCodingColumns,
  } = useColumnVisibility();
  const { selectedPaidol, selectedPaidolId } = useAppSelector(selectUserCompanies);

  // SEE-974/SEE-972: gates the equipment coding columns (Transaction type,
  // Equipment, Equipment Cost Code, Equipment Cost Type). Default off; turned on
  // per-paidol via the AppConfig equipment.enableEquipmentCoding flag.
  const { 'equipment.enableEquipmentCoding': isEquipmentCodingEnabled } = useIsEnabled([
    { featureName: 'equipment', flagName: 'enableEquipmentCoding' },
  ]);

  // Equipment coding columns are registered in COLUMN_DEFINITIONS (Vista-only)
  // but only surface when the flag is on. Strip them here so they flow through
  // the normal visibility/ordering pipeline exactly like every other column.
  const visibleColumnIds = useMemo(
    () =>
      isEquipmentCodingEnabled
        ? rawVisibleColumnIds
        : rawVisibleColumnIds.filter((id) => !EQUIPMENT_CODING_COLUMN_IDS.includes(id)),
    [rawVisibleColumnIds, isEquipmentCodingEnabled]
  );
  const isVisible = useCallback(
    (columnId: CodingColumnId) =>
      (isEquipmentCodingEnabled || !EQUIPMENT_CODING_COLUMN_IDS.includes(columnId)) && rawIsVisible(columnId),
    [rawIsVisible, isEquipmentCodingEnabled]
  );

  const savedPrefs = useAppSelector((state) => selectColumnPreferences(state, selectedPaidolId));

  // Apply user preferences — produces a unified flat ordered list of visible configurable columns
  const { orderedVisibleIds } = useMemo(
    () =>
      reconcilePreferences(
        (Array.isArray(savedPrefs) ? savedPrefs : []) as ColumnPreferenceEntry[],
        visibleColumnIds
      ),
    [savedPrefs, visibleColumnIds]
  );

  // Extract only coding columns from the user-ordered list (for moduleColumnProps)
  const orderedCodingColumnIds = useMemo(
    () =>
      orderedVisibleIds.filter((id) => visibleColumnIds.includes(id as CodingColumnId)) as CodingColumnId[],
    [orderedVisibleIds, visibleColumnIds]
  );

  const userOrderedCodingSet = useMemo(() => new Set(orderedCodingColumnIds), [orderedCodingColumnIds]);
  const userIsVisible = useMemo(
    () => (columnId: CodingColumnId) => userOrderedCodingSet.has(columnId),
    [userOrderedCodingSet]
  );

  const codingConfig = useMemo(() => {
    const moduleColumnProps: ModuleColumnProps = {
      isVisible: userIsVisible,
      getLabel,
      readOnly,
      orderedColumnIds: orderedCodingColumnIds,
      isEquipmentCodingEnabled,
    };

    if (codingSystemType === 'agave') {
      return {
        codingSystemType: 'agave' as const,
        dropdownModule: agaveDropdownModule,
        moduleColumnProps,
        isAgaveClient: true,
        isFlexAccount: false,
        isJobCodeAccount: false,
      };
    }
    if (codingSystemType === 'flex') {
      return {
        codingSystemType: 'flex' as const,
        dropdownModule: flexDropdownModule,
        moduleColumnProps,
        isAgaveClient: false,
        isFlexAccount: true,
        isJobCodeAccount: false,
      };
    }
    if (codingSystemType === 'jobCode') {
      return {
        codingSystemType: 'jobCode' as const,
        dropdownModule: jobCodeDropdownModule,
        moduleColumnProps,
        isAgaveClient: false,
        isFlexAccount: false,
        isJobCodeAccount: true,
      };
    }
    return {
      codingSystemType: null,
      dropdownModule: undefined,
      moduleColumnProps,
      isAgaveClient: false,
      isFlexAccount: false,
      isJobCodeAccount: false,
    };
  }, [codingSystemType, userIsVisible, getLabel, orderedCodingColumnIds, readOnly, isEquipmentCodingEnabled]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getMissingRequired = useCallback(
    (transaction: Record<string, any> | null | undefined): string[] => {
      if (!selectedPaidol || !codingSystemType) return [];
      return getMissingRequiredFields(transaction, selectedPaidol, codingSystemType);
    },
    [selectedPaidol, codingSystemType]
  );

  return {
    ...codingConfig,
    isVisible,
    getLabel,
    visibleColumnIds,
    hasCodingColumns,
    codingSystemType,
    orderedVisibleIds,
    getMissingRequired,
  };
}

export default useCodingSystemConfig;
